// Chart › Finance › Money per seat (`/c/{slug}/chart/finance/margins`; 11 §Revenue attribution,
// M5-03 `finance.margins`; Chart UX pass 2026-10-21): cost, revenue and margin per seat, team or
// client for a month, as four compact tiles over one table whose cost column carries an inline
// bar against the biggest row and whose seat rows name the person holding the seat. A seat's
// revenue is what is posted on it plus its share of its clients' revenue; a client's cost is its
// seats' cost by share. Restricted (it carries pay and revenue): the owner and finance admins;
// others get the notice and no call. Two calls on load: the caller's finance standing, then the
// view.
import {
  buttonVariants,
  cn,
  EmptyState,
  InlineBar,
  type InlineBarTone,
  Segmented,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, CoinsIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { SeatAvatar } from "../chart/chart-parts";
import type { CompanyAccess } from "../company";
import { monthOfWeek } from "../de/cost-tab";
import { formatMoney } from "../format";
import { LoadError, Loading, usePeriod } from "../screen-parts";
import {
  FinancePage,
  MonthSelect,
  monthLabel,
  monthsAround,
  RestrictedNotice,
  ratioText,
  useFinanceStanding,
  useOrgNames,
} from "./finance-parts";
import { type MarginBy, marginsQuery } from "./finance-queries";

const BY_LABELS: Record<MarginBy, string> = { seat: "Seat", team: "Team", client: "Client" };

/** A number with a thin bar under it, scaled to `max` (the biggest row). */
function BarCell({
  value,
  max,
  text,
  tone = "primary",
}: {
  value: number;
  max: number;
  text: string;
  tone?: InlineBarTone;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <span className="flex flex-col items-end gap-1">
      <span className="tabular-nums">{text}</span>
      <InlineBar value={pct} size="xs" tone={tone} className="w-24" aria-hidden="true" />
    </span>
  );
}

export function MarginsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const focusMonth = monthOfWeek(usePeriod());
  const months = useMemo(() => monthsAround(focusMonth, 12, 0), [focusMonth]);
  const [month, setMonth] = useState(focusMonth);
  const [by, setBy] = useState<MarginBy>("seat");
  const standing = useFinanceStanding(api, company);
  const names = useOrgNames(api, company);
  const view = useQuery({
    ...marginsQuery(api, company.id, month, by),
    enabled: standing.restricted,
  });
  const currency = view.data?.currency ?? "USD";
  const money = (minor: number) => formatMoney(minor, currency);
  const total = view.data?.total;
  const rows = [...(view.data?.rows ?? [])].sort(
    (a, b) => b.cost_minor - a.cost_minor || a.name.localeCompare(b.name),
  );
  const maxCost = Math.max(0, ...rows.map((r) => r.cost_minor));
  const maxRevenue = Math.max(0, ...rows.map((r) => r.revenue_minor));

  return (
    <FinancePage
      company={company}
      where="chart"
      title="Money per seat"
      icon={CoinsIcon}
      screen="finance-margins"
      {...(view.data ? { count: rows.length } : {})}
      toolbar={
        standing.restricted ? (
          <>
            <Segmented
              aria-label="Per"
              size="sm"
              value={by}
              onValueChange={setBy}
              options={(["seat", "team", "client"] as const).map((v) => ({
                value: v,
                label: `Per ${BY_LABELS[v].toLowerCase()}`,
              }))}
            />
            <MonthSelect value={month} onChange={setMonth} months={months} />
            <span className="text-xs text-muted-foreground">
              {currency} · margin % is the margin over revenue
            </span>
          </>
        ) : null
      }
    >
      {standing.query.isPending ? (
        <Loading label="Loading your finance access" rows={2} />
      ) : standing.query.isError ? (
        <LoadError what="Your finance access" error={standing.query.error} />
      ) : !standing.restricted ? (
        <RestrictedNotice company={company} what="Margins" />
      ) : view.isPending ? (
        <Loading label="Loading margins" rows={5} />
      ) : view.isError ? (
        <LoadError what="Margins" error={view.error} />
      ) : (
        <>
          {total ? (
            <StatGrid dense data-slot="margin-totals" aria-label={`${monthLabel(month)} totals`}>
              <StatTile
                size="sm"
                label="Revenue"
                value={money(total.revenue_minor)}
                detail={monthLabel(month)}
              />
              <StatTile size="sm" label="Cost" value={money(total.cost_minor)} />
              <StatTile
                size="sm"
                label="Margin"
                value={money(total.margin_minor)}
                detail={total.margin_minor < 0 ? "Costs above revenue" : "Revenue above costs"}
              />
              <StatTile size="sm" label="Margin %" value={ratioText(total.margin_ratio)} />
            </StatGrid>
          ) : null}
          {rows.length === 0 ? (
            <EmptyState
              variant="card"
              icon={CoinsIcon}
              title={by === "client" ? "No clients yet" : `No ${by}s yet`}
              description={
                by === "client"
                  ? "Add clients and the seats that serve them in Setup › Finance › Clients & revenue."
                  : "Seats and teams come from the org chart."
              }
            />
          ) : (
            <Table data-slot="margins">
              <TableCaption className="sr-only">
                {monthLabel(month)} in {currency}, per {BY_LABELS[by].toLowerCase()}. Margin % is
                the margin over revenue.
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">{BY_LABELS[by]}</TableHead>
                  <TableHead scope="col" className="text-right">
                    Cost
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Revenue
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Margin
                  </TableHead>
                  <TableHead scope="col" className="text-right max-sm:hidden">
                    Margin %
                  </TableHead>
                  <TableHead scope="col" className="w-10">
                    <span className="sr-only">Open</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const seat = r.subject_kind === "seat" ? names.seatOf(r.subject_id) : undefined;
                  return (
                    <TableRow key={r.subject_id} data-subject={r.subject_id} className="group/row">
                      <TableHead scope="row" className="py-1.5 font-normal">
                        <span className="flex min-w-0 items-center gap-2">
                          {seat ? <SeatAvatar seat={seat} size="sm" /> : null}
                          {r.subject_kind === "seat" ? (
                            <Link
                              to="/c/$slug/kpis/chart/seat/$id"
                              params={{ slug: company.slug, id: r.subject_id }}
                              search={keepFocus}
                              className="flex min-w-0 items-baseline gap-1.5 underline-offset-4 hover:underline"
                            >
                              {seat?.holders.length ? (
                                <>
                                  <span className="truncate font-medium">
                                    {seat.holders.map((h) => h.display_name).join(", ")}
                                  </span>
                                  <span className="truncate text-xs text-muted-foreground">
                                    {r.name}
                                  </span>
                                </>
                              ) : (
                                <span className="truncate">{r.name}</span>
                              )}
                            </Link>
                          ) : (
                            <span className="truncate font-medium">{r.name}</span>
                          )}
                        </span>
                      </TableHead>
                      <TableCell className="py-1.5 text-right">
                        <BarCell value={r.cost_minor} max={maxCost} text={money(r.cost_minor)} />
                      </TableCell>
                      <TableCell className="py-1.5 text-right">
                        <BarCell
                          value={r.revenue_minor}
                          max={maxRevenue}
                          text={money(r.revenue_minor)}
                          tone="success"
                        />
                      </TableCell>
                      <TableCell
                        className={cn(
                          "py-1.5 text-right tabular-nums",
                          r.margin_minor < 0 ? "text-danger-text" : "text-success-text",
                        )}
                      >
                        {r.margin_minor < 0 ? "−" : ""}
                        {money(Math.abs(r.margin_minor))}
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums max-sm:hidden">
                        {ratioText(r.margin_ratio)}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {r.subject_kind === "seat" ? (
                          <Link
                            to="/c/$slug/kpis/chart/seat/$id"
                            params={{ slug: company.slug, id: r.subject_id }}
                            search={keepFocus}
                            aria-label={`Open ${r.name}`}
                            title="Open seat"
                            className={cn(
                              buttonVariants({ variant: "ghost", size: "icon-xs" }),
                              "opacity-0 transition-opacity duration-(--dur-fast) group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100",
                            )}
                          >
                            <ArrowUpRightIcon aria-hidden="true" />
                          </Link>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              {total ? (
                <TableFooter>
                  <TableRow>
                    <TableHead scope="row">Company</TableHead>
                    <TableCell className="text-right tabular-nums">
                      {money(total.cost_minor)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(total.revenue_minor)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(total.margin_minor)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:hidden">
                      {ratioText(total.margin_ratio)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          )}
        </>
      )}
    </FinancePage>
  );
}
